(() => {
  if (window.__taktHomeUIRegistered) return;
  window.__taktHomeUIRegistered = true;
  const mounted = new WeakMap();
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  function mountHero(root) {
    const videos = [...root.querySelectorAll('video')];
    if (!videos.length) return () => {};
    const primary = root.querySelector('[data-takt-hero-video]') || videos[videos.length - 1];
    const events = new AbortController();
    const options = { signal: events.signal };
    const control = document.createElement('button');
    control.type = 'button';
    control.className = 'takt-hr__video-control takt-preview-video-control';
    let requested = !reducedMotion();
    let inView = true;
    let playbackSequence = 0;
    const update = () => {
      const playing = !primary.paused && !primary.ended;
      control.textContent = playing ? root.dataset.pauseLabel || 'Pause video' : root.dataset.playLabel || 'Play video';
      control.setAttribute('aria-label', control.textContent);
      control.setAttribute('aria-pressed', String(playing));
    };
    const pause = () => { playbackSequence++; videos.forEach(video => video.pause()); update(); };
    const play = async () => {
      if (!requested || !inView || document.hidden || !root.isConnected) return;
      const attempt = ++playbackSequence;
      const started = await Promise.allSettled(videos.map(video => {
        video.muted = true;
        return video.play();
      }));
      if (!requested || !inView || document.hidden || !root.isConnected) { pause(); return; }
      if (attempt !== playbackSequence) return;
      if (!primary.paused && primary.readyState >= 2) primary.style.opacity = '1';
      if (started[videos.indexOf(primary)]?.status === 'rejected') update();
    };
    control.addEventListener('click', () => {
      requested = primary.paused;
      if (requested) play(); else pause();
    }, options);
    videos.forEach(video => {
      video.muted = true;
      if (!video.paused && video.readyState >= 2) video.style.opacity = '1';
      video.addEventListener('playing', () => { video.style.opacity = '1'; update(); }, options);
      video.addEventListener('pause', update, options);
      video.addEventListener('error', update, options);
    });
    document.addEventListener('visibilitychange', () => document.hidden ? pause() : play(), options);
    const mediaQuery = matchMedia('(prefers-reduced-motion: reduce)');
    mediaQuery.addEventListener('change', event => { if (event.matches) { requested = false; pause(); } }, options);
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      inView = entries[0].isIntersecting;
      if (inView) play(); else pause();
    }, { threshold: 0.05 }) : null;
    observer?.observe(root);
    root.append(control);
    update();
    if (requested) play(); else pause();
    return () => { events.abort(); observer?.disconnect(); pause(); control.remove(); };
  }

  function mountFeature(root) {
    const data = root.querySelector('[data-variant-data]');
    if (!data) return () => {};
    let variants;
    try { variants = new Map(JSON.parse(data.textContent).map(variant => [String(variant.id), variant])); }
    catch { return () => {}; }
    const events = new AbortController();
    const options = { signal: events.signal };
    const buttons = [...root.querySelectorAll('[data-variant-id]')];
    const mainImage = root.querySelector('.takt-feature__media img');
    const media = root.querySelector('[data-media]');
    const name = root.querySelector('[data-color-name]');
    const price = root.querySelector('[data-price]');
    let imageError = root.querySelector('[data-image-error]');
    if (!imageError) {
      imageError = document.createElement('p');
      imageError.className = 'takt-feature__image-error';
      imageError.setAttribute('role', 'status');
      imageError.hidden = true;
      (name?.parentElement || root.querySelector('.takt-feature__panel'))?.append(imageError);
    }
    const clearImageError = () => { imageError.hidden = true; imageError.textContent = ''; };
    const showImageError = () => {
      imageError.textContent = root.dataset.imageErrorLabel || 'Image unavailable. Open product details to view this colour.';
      imageError.hidden = false;
    };
    const input = root.querySelector('[data-variant-input]');
    const view = root.querySelector('[data-view]');
    const add = root.querySelector('[data-add]');
    const availableLabel = add?.textContent.trim();
    let sequence = 0;
    buttons.forEach(button => {
      button.setAttribute('aria-pressed', String(button.classList.contains('is-active')));
      button.addEventListener('click', () => {
        const variant = variants.get(button.dataset.variantId);
        if (!variant) return;
        const request = ++sequence;
        clearImageError();
        media?.removeAttribute('aria-busy');
        media?.classList.remove('is-swapping');
        buttons.forEach(other => {
          const active = other === button;
          other.classList.toggle('is-active', active);
          other.setAttribute('aria-pressed', String(active));
        });
        if (input) input.value = variant.id;
        if (price) price.textContent = variant.price;
        if (name) name.textContent = variant.name;
        if (view) {
          const url = new URL(view.href, location.href);
          url.searchParams.set('variant', variant.id);
          view.href = url.toString();
        }
        if (add) {
          add.disabled = !variant.available;
          add.textContent = variant.available ? root.dataset.addLabel || availableLabel : root.dataset.soldLabel || 'Sold out';
        }
        if (!mainImage || !media) return;
        if (!variant.image) { showImageError(); return; }
        media.setAttribute('aria-busy', 'true');
        const nextImage = new Image();
        nextImage.onload = () => {
          if (request !== sequence || !root.isConnected) return;
          mainImage.removeAttribute('srcset');
          mainImage.removeAttribute('sizes');
          mainImage.src = variant.image;
          mainImage.alt = variant.name;
          clearImageError();
          media.removeAttribute('aria-busy');
          media.classList.remove('is-swapping');
        };
        nextImage.onerror = () => {
          if (request !== sequence || !root.isConnected) return;
          showImageError();
          media.removeAttribute('aria-busy');
          media.classList.remove('is-swapping');
        };
        nextImage.src = variant.image;
      }, options);
    });
    return () => { sequence++; events.abort(); media?.removeAttribute('aria-busy'); };
  }

  function mountHeader(root) {
    const scope = root.closest('.shopify-section') || root.parentElement;
    const drawer = scope.querySelector('[data-takt-drawer]');
    const panel = drawer?.querySelector('[role="dialog"]');
    const open = root.querySelector('[data-takt-drawer-open]');
    const search = root.querySelector('.takt-search');
    const searchToggle = root.querySelector('[data-takt-search-toggle]');
    const events = new AbortController();
    const options = { signal: events.signal };
    let overflow = '';
    let restoreFocus = null;
    const focusable = () => [...panel.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), [tabindex="0"]')].filter(el => el.getClientRects().length);
    const closeSearch = (restore = true) => {
      if (!search || search.hidden) return;
      search.hidden = true;
      search.classList.remove('is-open');
      searchToggle?.setAttribute('aria-expanded', 'false');
      if (restore) searchToggle?.focus();
    };
    const closeDrawer = (restore = true) => {
      if (!drawer?.classList.contains('is-open')) return;
      drawer.classList.remove('is-open');
      drawer.inert = true;
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = overflow;
      open?.setAttribute('aria-expanded', 'false');
      if (restore && restoreFocus?.isConnected) restoreFocus.focus();
    };
    if (drawer) { drawer.inert = true; drawer.setAttribute('aria-hidden', 'true'); }
    open?.addEventListener('click', () => {
      if (!drawer || !panel) return;
      closeSearch(false);
      restoreFocus = document.activeElement;
      overflow = document.body.style.overflow;
      drawer.inert = false;
      drawer.removeAttribute('aria-hidden');
      drawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      open.setAttribute('aria-expanded', 'true');
      panel.setAttribute('tabindex', '-1');
      (focusable()[0] || panel).focus({ preventScroll: true });
    }, options);
    drawer?.querySelectorAll('[data-takt-drawer-close]').forEach(button => button.addEventListener('click', () => closeDrawer(), options));
    searchToggle?.addEventListener('click', () => {
      if (!search) return;
      if (!search.hidden) { closeSearch(); return; }
      closeDrawer(false);
      search.hidden = false;
      search.classList.add('is-open');
      searchToggle.setAttribute('aria-expanded', 'true');
      search.querySelector('input[type="search"]')?.focus();
    }, options);
    search?.querySelector('[data-takt-search-close]')?.addEventListener('click', () => closeSearch(), options);
    const closeSubmenus = () => root.querySelectorAll('.takt-header__item.is-open').forEach(item => {
      item.classList.remove('is-open');
      item.querySelector('[data-takt-submenu-toggle]')?.setAttribute('aria-expanded', 'false');
    });
    if (root.dataset.menuStyle === 'click') {
      root.querySelectorAll('[data-takt-submenu-toggle]').forEach(toggle => toggle.addEventListener('click', event => {
        event.stopPropagation();
        const item = toggle.closest('.takt-header__item');
        const next = !item.classList.contains('is-open');
        closeSubmenus();
        item.classList.toggle('is-open', next);
        toggle.setAttribute('aria-expanded', String(next));
      }, options));
      document.addEventListener('click', event => { if (!root.contains(event.target)) closeSubmenus(); }, options);
    }
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') {
        closeDrawer(); closeSearch(); closeSubmenus();
      }
      if (event.key !== 'Tab' || !drawer?.classList.contains('is-open')) return;
      const all = focusable(), first = all[0], last = all[all.length - 1];
      if (!first) { event.preventDefault(); panel.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !panel.contains(document.activeElement))) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) {
        event.preventDefault(); first.focus();
      }
    }, options);
    return () => { closeDrawer(false); events.abort(); };
  }

  const definitions = [
    ['takt-hero-controller', '[id^="TaktHero-"]', mountHero],
    ['takt-feature-controller', '[id^="TaktFeature-"]', mountFeature],
    ['takt-header-controller', '[id^="TaktHeader-"]', mountHeader]
  ];
  const mount = (root, create) => {
    if (!mounted.has(root)) mounted.set(root, create(root));
  };
  definitions.forEach(([name, selector, create]) => {
    if (!customElements.get(name)) customElements.define(name, class extends HTMLElement {
      connectedCallback() { mount(this, create); }
      disconnectedCallback() { mounted.get(this)?.(); mounted.delete(this); }
    });
    document.querySelectorAll(selector).forEach(root => mount(root, create));
  });
  document.addEventListener('shopify:section:load', event => definitions.forEach(([, selector, create]) => event.target.querySelectorAll(selector).forEach(root => mount(root, create))));
  document.addEventListener('shopify:section:unload', event => definitions.forEach(([, selector]) => event.target.querySelectorAll(selector).forEach(root => { mounted.get(root)?.(); mounted.delete(root); })));
})();
